import { WatchLaterTwoTone as HourglassTopIcon } from "@mui/icons-material";
import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useGlobalEffectActions } from "@ryde/redux";
import {
  nearbyFilteringRideIdAtom,
  nearbyOtherRidesIdsBatchAtom,
} from "@ryde/shared";

import { FramerTooltip } from "../tooltips";

interface IRidesNearbyFunnel {
  rideId: number;
}

export const RidesNearbyFunnel: FC<IRidesNearbyFunnel> = ({ rideId }) => {
  const { t } = useTranslation();
  const filteringRideId = useRecoilValue(nearbyFilteringRideIdAtom);
  const setFilteringRideId = useSetRecoilState(nearbyFilteringRideIdAtom);
  const filteredRidesIds = useRecoilValue(nearbyOtherRidesIdsBatchAtom);
  const filteredRidesIdsByRideId = useMemo(
    () => filteredRidesIds?.data?.[rideId] ?? [],
    [filteredRidesIds, rideId],
  );

  const isNearByLoading = useMemo(
    () => filteredRidesIds.isLoading,
    [filteredRidesIds],
  );
  const { resetPaginationAppEvent } = useGlobalEffectActions();
  const ref = useRef(null);
  const handleNearbyClick = useEvent((): void => {
    setFilteringRideId(rideId);
    resetPaginationAppEvent();
  });
  const handleClearNearbyRides = useEvent((): void => {
    setFilteringRideId(null);
  });

  const isCurrentFilteringRide = useMemo(() => {
    return !!filteringRideId && filteringRideId === rideId;
  }, [filteringRideId, rideId]);

  const hasNearby = useMemo(() => {
    return (
      !isNearByLoading &&
      !isCurrentFilteringRide &&
      Boolean(filteredRidesIdsByRideId?.length)
    );
  }, [
    filteredRidesIdsByRideId?.length,
    isCurrentFilteringRide,
    isNearByLoading,
  ]);

  return (
    <span className="flexCenterVertical" ref={ref}>
      {!isNearByLoading && isCurrentFilteringRide && (
        <SVG_MUI.FilterFilledSVG
          onClick={handleClearNearbyRides}
          className="pointer nearby_funnel"
          data-e2e="funnel_filled"
        />
      )}

      {!isNearByLoading && !isCurrentFilteringRide && !hasNearby && (
        <FramerTooltip content={t("general.NO_RESULTS")}>
          <SVG_MUI.Filter2SVG
            className="nearby_funnel"
            data-e2e="funnel_outline"
          />
        </FramerTooltip>
      )}

      {!isNearByLoading && !isCurrentFilteringRide && hasNearby && (
        <SVG_MUI.FunnelHasNearby5SVG
          onClick={handleNearbyClick}
          className="pointer nearby_funnel"
          data-e2e="funnel_outline"
        />
      )}

      {isNearByLoading && (
        <HourglassTopIcon sx={{ fontSize: "18px" }} data-e2e="loading_nearby" />
      )}
    </span>
  );
};
